import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  createLabelBatch,
  getLabelBatch,
  fetchLabelPdf,
  listLabelBatches,
  pdfUrl,
  type LabelBatch,
} from "@/lib/inventory/api";
import { useInventoryText } from "@/lib/inventory/i18n";
import {
  printableLabelLayout,
  useLabelText,
} from "@/lib/inventory/labelLayout";
import {
  ConfirmReview,
  ErrorNotice,
  Field,
  Loading,
  Panel,
  Pager,
  useInventoryAccess,
  useInventoryWrite,
  useRead,
} from "./shared";

export function InventoryLabels({ onScan }: { onScan: (id: string) => void }) {
  const t = useInventoryText();
  const labelText = useLabelText();
  const access = useInventoryAccess();
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const batches = useRead(() => listLabelBatches(limit, offset), [offset]);
  const write = useInventoryWrite();
  const [quantity, setQuantity] = useState(5);
  const [review, setReview] = useState(false);
  const [issued, setIssued] = useState<LabelBatch | null>(null);
  const [printBatch, setPrintBatch] = useState<LabelBatch | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [layout, setLayout] = useState({
    rows: 5,
    columns: 2,
    margin_pt: 36,
    module_pt: 1,
  });
  const valid = Number.isInteger(quantity) && quantity >= 1 && quantity <= 500;
  const layoutFields = [
    ["rows", t.rows, 1, 10, 1],
    ["columns", t.columns, 1, 3, 1],
    ["margin_pt", t.margin, 18, 72, 1],
    ["module_pt", t.module, 0.75, 2, 0.05],
  ] as const;
  const layoutValid = printableLabelLayout(layout);
  const printable = (batch: LabelBatch) =>
    printableLabelLayout(
      layout,
      batch.batch_id === printBatch?.batch_id ? printBatch : batch,
    );
  const clipping =
    !layoutValid ||
    (issued !== null && !printable(issued)) ||
    batches.data?.batches.some((batch) => !printable(batch));
  async function download(batchId: string) {
    if (pdfBusy || !access.read) return;
    setPdfBusy(true);
    setPdfError(false);
    try {
      const exact = await getLabelBatch(batchId);
      if (!mounted.current) return;
      if (exact.batch_id !== batchId) throw new Error("Batch read-back failed");
      setPrintBatch(exact);
      if (!printableLabelLayout(layout, exact)) return;
      const blob = await fetchLabelPdf(batchId, layout);
      if (!mounted.current) return;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${batchId}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Allow the browser to start consuming the download before revoking it.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      if (mounted.current) setPdfError(true);
    } finally {
      if (mounted.current) setPdfBusy(false);
    }
  }
  async function generate() {
    if (!access.write || !valid || batches.error) return;
    const result = await write.run({ quantity }, async (key) => {
      const created = await createLabelBatch(quantity, undefined, key);
      const confirmed = await getLabelBatch(created.batch_id);
      if (
        confirmed.batch_id !== created.batch_id ||
        confirmed.quantity !== quantity
      )
        throw new Error("Batch read-back failed");
      return confirmed;
    });
    if (result) {
      setIssued(result);
      setReview(false);
      if (offset === 0) batches.refresh();
      else setOffset(0);
    }
  }
  return (
    <Panel title={t.labels}>
      <p>{t.labelHelp}</p>
      {!access.write && <p>{t.readOnly}</p>}
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t.quantity}>
          <Input
            type="number"
            min={1}
            max={500}
            value={Number.isFinite(quantity) ? quantity : ""}
            disabled={write.busy || review || write.unresolved}
            onChange={(e) => {
              if (!write.busy && !review && !write.unresolved)
                setQuantity(
                  e.target.value === "" ? NaN : Number(e.target.value),
                );
            }}
          />
        </Field>
        <Button
          disabled={
            !access.write ||
            !valid ||
            write.busy ||
            review ||
            !!batches.error ||
            batches.busy ||
            write.blocked
          }
          onClick={() => setReview(true)}
        >
          {t.review}
        </Button>
        <Button
          variant="secondary"
          disabled={write.busy || batches.busy}
          onClick={() => {
            setReview(false);
            write.clear();
            batches.refresh();
          }}
        >
          {t.retry}
        </Button>
      </div>
      <ErrorNotice error={write.error || batches.error} />
      {write.unresolved && <p role="status">{labelText.recovery}</p>}
      {review && (
        <ConfirmReview
          title={t.labels}
          busy={write.busy}
          blocked={write.blocked || !access.write || !!batches.error}
          onConfirm={() => void generate()}
          onCancel={() => setReview(false)}
        >
          <p>
            {t.quantity}: {quantity}
          </p>
          <p>{t.labelHelp}</p>
        </ConfirmReview>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {layoutFields.map(([key, label, min, max, step]) => (
          <Field key={key} label={label}>
            <Input
              type="number"
              min={min}
              max={max}
              step={step}
              value={Number.isFinite(layout[key]) ? layout[key] : ""}
              onChange={(e) =>
                setLayout((v) => ({
                  ...v,
                  [key]: e.target.value === "" ? NaN : Number(e.target.value),
                }))
              }
            />
          </Field>
        ))}
      </div>
      {clipping && <p role="alert">{labelText.layoutError}</p>}
      {pdfError && <p role="alert">{labelText.pdfError}</p>}
      {pdfBusy && <Loading />}
      <p className="rounded border border-border p-3 text-sm">{t.printHelp}</p>
      {issued && (
        <div className="space-y-3">
          <h3 className="font-mono">{issued.batch_id}</h3>
          {layoutValid && printable(issued) && (
            <a
              className="text-accent-indigo underline"
              href={pdfUrl(issued.batch_id, layout)}
              aria-disabled={pdfBusy}
              onClick={(event) => {
                event.preventDefault();
                void download(issued.batch_id);
              }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.pdf}
            </a>
          )}
          <ul>
            {issued.codes?.map((code) => (
              <li key={code} className="flex flex-wrap items-center gap-3 py-1">
                <code>{code}</code>
                <Button variant="secondary" onClick={() => onScan(code)}>
                  {t.scan}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <h3 className="font-510">{t.batches}</h3>
      {batches.busy && <Loading />}
      {batches.data?.batches.length === 0 && <p>{t.empty}</p>}
      <ul className="divide-y divide-border">
        {batches.data?.batches.map((batch) => (
          <li
            key={batch.batch_id}
            className="flex flex-wrap items-center justify-between gap-3 py-3"
          >
            <div>
              <code>{batch.batch_id}</code>
              <p className="text-sm">
                {t.quantity}: {batch.quantity} · {batch.created_at}
              </p>
            </div>
            {layoutValid && printable(batch) && (
              <a
                className="text-accent-indigo underline"
                target="_blank"
                rel="noopener noreferrer"
                href={pdfUrl(batch.batch_id, layout)}
                aria-disabled={pdfBusy}
                onClick={(event) => {
                  event.preventDefault();
                  void download(batch.batch_id);
                }}
              >
                {t.reprint}
              </a>
            )}
          </li>
        ))}
      </ul>
      {batches.data && Number.isInteger(batches.data.total) && (
        <Pager
          offset={batches.data.offset}
          total={batches.data.total}
          limit={limit}
          onChange={setOffset}
          busy={batches.busy}
        />
      )}
    </Panel>
  );
}
